import { useCallback, useEffect, useRef, useState } from 'react';
export function useChamada(sinal, buscarServidores) {
    const [estado, setEstado] = useState('parada');
    const [comQuem, setComQuem] = useState(null);
    const [duracao, setDuracao] = useState(0);
    const [mudo, setMudo] = useState(false);
    const [erro, setErro] = useState(null);
    const conexao = useRef(null);
    const microfone = useRef(null);
    const audio = useRef(null);
    const chamadaAtual = useRef(null);
    /**
     * Caminhos de rede que chegaram antes de a descrição de mídia estar posta.
     *
     * Acontece o tempo todo: o outro lado começa a mandar candidatos assim que gera a
     * oferta, e eles podem bater aqui antes do `setRemoteDescription`. Aplicar um candidato
     * antes disso derruba a conexão com um erro que parece aleatório.
     */
    const rotasEsperando = useRef([]);
    /**
     * A oferta de quem ligou, guardada até a pessoa atender.
     *
     * Aplicá-la na hora em que chega abriria o microfone de quem ainda não decidiu se quer
     * falar — e o navegador acenderia a luz da câmera/microfone sem ninguém ter aceitado.
     */
    const descricaoPendente = useRef(null);
    const sinalRef = useRef(sinal);
    sinalRef.current = sinal;
    const limpar = useCallback(() => {
        conexao.current?.close();
        conexao.current = null;
        microfone.current?.getTracks().forEach((t) => t.stop());
        microfone.current = null;
        if (audio.current) {
            audio.current.srcObject = null;
            audio.current.remove();
            audio.current = null;
        }
        rotasEsperando.current = [];
        // A oferta guardada tem de ir embora com a chamada: deixá-la aqui faria a PRÓXIMA
        // ligação recebida tentar responder à descrição de mídia da anterior.
        descricaoPendente.current = null;
        chamadaAtual.current = null;
        setEstado('parada');
        setComQuem(null);
        setDuracao(0);
        setMudo(false);
    }, []);
    /** O relógio da conversa só anda depois de atendida. */
    useEffect(() => {
        if (estado !== 'falando')
            return;
        const id = setInterval(() => setDuracao((d) => d + 1), 1000);
        return () => clearInterval(id);
    }, [estado]);
    useEffect(() => () => limpar(), [limpar]);
    const prepararConexao = useCallback(async () => {
        const servidores = await buscarServidores();
        const pc = new RTCPeerConnection({ iceServers: servidores });
        // O áudio que chega toca num elemento criado na hora e removido no fim: sem um nó de
        // áudio no documento, o navegador recebe a trilha e não a reproduz.
        pc.ontrack = (evento) => {
            if (!audio.current) {
                audio.current = document.createElement('audio');
                audio.current.autoplay = true;
                document.body.appendChild(audio.current);
            }
            audio.current.srcObject = evento.streams[0] ?? null;
            void audio.current.play().catch(() => undefined);
        };
        pc.onicecandidate = (evento) => {
            if (evento.candidate && chamadaAtual.current) {
                sinalRef.current.rota(chamadaAtual.current, evento.candidate.toJSON());
            }
        };
        pc.onconnectionstatechange = () => {
            if (pc.connectionState === 'connected')
                setEstado('falando');
            if (pc.connectionState === 'failed') {
                setErro('A chamada não completou. Pode ser a rede de um dos lados.');
                if (chamadaAtual.current)
                    sinalRef.current.fim(chamadaAtual.current, 'falhou');
                limpar();
            }
        };
        const captura = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
        microfone.current = captura;
        captura.getTracks().forEach((trilha) => pc.addTrack(trilha, captura));
        conexao.current = pc;
        return pc;
    }, [buscarServidores, limpar]);
    const ligar = useCallback(async (dados) => {
        setErro(null);
        chamadaAtual.current = dados.chamadaId;
        setComQuem({ nome: dados.nome, avatarUrl: dados.avatarUrl });
        setEstado('chamando');
        try {
            const pc = await prepararConexao();
            const oferta = await pc.createOffer();
            await pc.setLocalDescription(oferta);
            sinalRef.current.sdp(dados.chamadaId, 'offer', oferta.sdp ?? '');
        }
        catch (e) {
            setErro(mensagemDeErro(e));
            if (chamadaAtual.current)
                sinalRef.current.fim(chamadaAtual.current, 'falhou');
            limpar();
        }
    }, [prepararConexao, limpar]);
    const atender = useCallback(async () => {
        const id = chamadaAtual.current;
        if (!id)
            return;
        setErro(null);
        setEstado('conectando');
        try {
            sinalRef.current.atendida(id);
            // A oferta já chegou e foi guardada em `rotasEsperando`/`descricaoPendente` — quem
            // responde só precisa produzir a resposta depois de aceitar.
            const pc = conexao.current ?? (await prepararConexao());
            if (descricaoPendente.current) {
                await pc.setRemoteDescription({ type: 'offer', sdp: descricaoPendente.current });
                descricaoPendente.current = null;
                await aplicarRotasGuardadas(pc);
            }
            const resposta = await pc.createAnswer();
            await pc.setLocalDescription(resposta);
            sinalRef.current.sdp(id, 'answer', resposta.sdp ?? '');
        }
        catch (e) {
            setErro(mensagemDeErro(e));
            sinalRef.current.fim(id, 'falhou');
            limpar();
        }
    }, [prepararConexao, limpar]);
    const aplicarRotasGuardadas = useCallback(async (pc) => {
        for (const candidato of rotasEsperando.current) {
            await pc.addIceCandidate(candidato).catch(() => undefined);
        }
        rotasEsperando.current = [];
    }, []);
    return {
        estado,
        comQuem,
        duracao,
        mudo,
        erro,
        ligar,
        atender,
        recusar: () => {
            if (chamadaAtual.current)
                sinalRef.current.fim(chamadaAtual.current, 'recusada');
            limpar();
        },
        desligar: () => {
            if (chamadaAtual.current)
                sinalRef.current.fim(chamadaAtual.current);
            limpar();
        },
        alternarMudo: () => {
            const trilhas = microfone.current?.getAudioTracks() ?? [];
            const novo = !mudo;
            trilhas.forEach((t) => (t.enabled = !novo));
            setMudo(novo);
        },
        aoReceberChamada: (dados) => {
            // Já estou em outra chamada: recuso a nova em vez de derrubar a que está em curso.
            if (chamadaAtual.current) {
                sinalRef.current.fim(dados.chamadaId, 'recusada');
                return;
            }
            chamadaAtual.current = dados.chamadaId;
            setComQuem({ nome: dados.nome, avatarUrl: dados.avatarUrl });
            setEstado('tocando');
        },
        aoReceberSdp: async (chamadaId, tipo, sdp) => {
            if (chamadaAtual.current !== chamadaId)
                return;
            if (tipo === 'offer') {
                // Guardada até a pessoa atender: aplicar a oferta antes disso abriria o microfone
                // de quem ainda não decidiu se quer falar.
                descricaoPendente.current = sdp;
                return;
            }
            const pc = conexao.current;
            if (!pc)
                return;
            await pc.setRemoteDescription({ type: 'answer', sdp });
            await aplicarRotasGuardadas(pc);
        },
        aoReceberRota: async (chamadaId, candidato) => {
            if (chamadaAtual.current !== chamadaId)
                return;
            const pc = conexao.current;
            if (!pc || !pc.remoteDescription) {
                rotasEsperando.current.push(candidato);
                return;
            }
            await pc.addIceCandidate(candidato).catch(() => undefined);
        },
        aoSerAtendida: (chamadaId) => {
            if (chamadaAtual.current === chamadaId)
                setEstado('conectando');
        },
        aoReceberFim: (chamadaId) => {
            if (chamadaAtual.current === chamadaId)
                limpar();
        },
    };
}
/** O erro do navegador, dito de um jeito que a pessoa entenda e saiba o que fazer. */
function mensagemDeErro(erro) {
    const nome = erro.name;
    if (nome === 'NotAllowedError') {
        return 'O navegador bloqueou o microfone. Libere o acesso e tente de novo.';
    }
    if (nome === 'NotFoundError')
        return 'Não achei microfone neste aparelho.';
    return 'Não consegui iniciar a chamada.';
}
//# sourceMappingURL=chamada.js.map